import breakpoints from '@/breakpoints';
import Card from '@/components/elements/Card/Card';
import Label from '@/components/elements/Label/Label';
import CardTags from '@/components/modules/place/CardTags';
import { StarRating } from '@/components/modules/rating';
import { config } from '@/config';
import { MEGA_MENU_NAVIGATION_FEATURE_FLAG } from '@/constants/experimentConstants';
import { Z_INDEX } from '@/constants/styleConstants';
import { SEARCH_HEADER_CONTENT_HEIGHT } from '@/features/search-header/SearchHeader';
import { trackPlaceClick } from '@/features/searchV2/helpers/tracking';
import { isProfilePageSubscription } from '@/helpers/placeHelperTS';
import withMobileView from '@/hoc/withMobileView';
import { useAppSelector } from '@/hooks';
import useExternalScript from '@/hooks/useExternalScript';
import useMobileView from '@/hooks/useMobileView';
import { PLACE_IMPRESSION, placeService } from '@/services';
import { SearchPlace } from '@/types/api/services/search';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import {
  addStylesheet,
  classnames,
  getDateFormattedForFirstAvailableTime,
  getImagePreview,
  getPlaceImage,
  isEmpty,
  isMobile,
  isServer,
  shouldDisplayReviews,
  splitCoordinates,
} from '../../../helpers';
import Carousel from './Carousel';
import placeStyles from './Map.module.scss';
import { MapTilerMarkers } from './MapTilerMarkers';

interface Position {
  lat: string | number;
  lon: string | number;
}

const getMapPosition = (isMobileView, useMegaMenu) => {
  if (useMegaMenu) {
    return isMobileView ? '112px' : `${SEARCH_HEADER_CONTENT_HEIGHT.DESKTOP}px`;
  }

  return isMobileView ? '166px' : '172px';
};

const shouldIncludeInMap = (point: Position): boolean => {
  // Check against Sweden's approximate bounds (lat min/max, lon min/max)
  const lat = parseFloat(String(point.lat));
  const lon = parseFloat(String(point.lon));
  return lat < 69.06 && lat > 55.336944 && lon < 24.166667 && lon > 10.9575;
};

const calculateMapViewParameters = ({
  bounds, // from searchV2 json format
  boundsFit,
  places, // from searchV2
  position, // user's current position
}) => {
  let boundsPadding = 50;
  let mapBounds = undefined;
  let center = [18.07144, 59.32421]; // Default Stockholm center

  // Priority 1: Use bounds from searchV2 if available and valid
  if (bounds) {
    try {
      const boundsObj = JSON.parse(bounds);
      // Basic validation: ensure coordinates are numbers and form a valid rectangle
      if (
        typeof boundsObj._southWest?.lng === 'number' &&
        typeof boundsObj._southWest?.lat === 'number' &&
        typeof boundsObj._northEast?.lng === 'number' &&
        typeof boundsObj._northEast?.lat === 'number' &&
        boundsObj._southWest.lng <= boundsObj._northEast.lng && // Ensure min <= max
        boundsObj._southWest.lat <= boundsObj._northEast.lat
      ) {
        mapBounds = [
          [boundsObj._southWest.lng, boundsObj._southWest.lat],
          [boundsObj._northEast.lng, boundsObj._northEast.lat],
        ];
      }
    } catch (error) {
      console.error('Error parsing searchV2.bounds:', error);
    }
  }

  // Priority 2: If the backend returns a boundsFit (the map fits the kommun shapes)
  if (boundsFit && boundsFit?.length === 4) {
    mapBounds = [
      [boundsFit[0], boundsFit[1]],
      [boundsFit[2], boundsFit[3]],
    ];
  }

  // Priority 3: If no valid searchV2.bounds && no boundsFit, calculate from places
  if (!mapBounds && places && places.length > 0) {
    let minLon = Infinity,
      maxLon = -Infinity,
      minLat = Infinity,
      maxLat = -Infinity;
    let hasValidPlaces = false;

    places.forEach((place) => {
      if (
        place.contact &&
        place.contact.position &&
        place.contact.position.lat &&
        place.contact.position.lon &&
        shouldIncludeInMap(place.contact.position)
      ) {
        const lat = parseFloat(place.contact.position.lat);
        const lon = parseFloat(place.contact.position.lon);
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        hasValidPlaces = true;
      }
    });

    if (hasValidPlaces) {
      mapBounds = [
        [minLon, minLat],
        [maxLon, maxLat],
      ];
    }

    // Set center based on user position if using places calculation
    const userPosition = splitCoordinates(position || '');
    if (userPosition.length === 2) {
      center = [parseFloat(String(userPosition[1])), parseFloat(String(userPosition[0]))];
    }
  }

  // Priority 4: Fallback to default Sweden bounds if neither bounds nor places yield results
  if (isEmpty(mapBounds)) {
    mapBounds = [
      [11.9723388, 55.5979967],
      [22.1545943, 65.5858387],
    ];
  }

  // Ensure mapBounds are valid (e.g., min/max are not identical if it's a single point)
  if (mapBounds) {
    const epsilon = 0.00001; // Small value
    if (mapBounds[0][0] === mapBounds[1][0]) {
      mapBounds[1][0] += epsilon;
    }
    if (mapBounds[0][1] === mapBounds[1][1]) {
      mapBounds[1][1] += epsilon;
    }
  }

  return {
    mapBounds,
    center,
    boundsPadding,
  };
};

const boundsObjectSchema = z.object({
  _southWest: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
  _northEast: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
});

type BoundsObject = z.infer<typeof boundsObjectSchema>;

const roundBoundsObject = (boundsObj: BoundsObject, decimals: number): BoundsObject | null => {
  if (!boundsObj || !boundsObj._southWest || !boundsObj._northEast) {
    return null; // Return null or handle invalid input as appropriate
  }

  const roundedObj: BoundsObject = {
    _southWest: {
      lat: parseFloat(boundsObj._southWest.lat.toFixed(decimals)),
      lng: parseFloat(boundsObj._southWest.lng.toFixed(decimals)),
    },
    _northEast: {
      lat: parseFloat(boundsObj._northEast.lat.toFixed(decimals)),
      lng: parseFloat(boundsObj._northEast.lng.toFixed(decimals)),
    },
  };

  return roundedObj;
};

const roundBounds = (boundsJsonString: string | undefined, decimals: number): string | null => {
  if (!boundsJsonString) {
    return null;
  }

  const boundsObj = boundsObjectSchema.safeParse(JSON.parse(boundsJsonString || '{}')); // Validate the JSON structure

  if (boundsObj.data) {
    const roundedObj = roundBoundsObject(boundsObj.data, decimals);
    return roundedObj ? JSON.stringify(roundedObj) : null;
  }

  return null;
};

function DynamicMap(props) {
  const [scriptLoaded] = useExternalScript({
    src: 'https://cdn.maptiler.com/maptiler-sdk-js/v3.2.0/maptiler-sdk.umd.min.js',
    cleanup: false,
  });
  addStylesheet('https://cdn.maptiler.com/maptiler-sdk-js/v3.2.0/maptiler-sdk.css');

  const searchV2 = useAppSelector((state) => state.searchV2);
  const flags = useAppSelector((state) => state.flags);
  const { isMobileView } = useMobileView();
  const useMegaMenu = flags[MEGA_MENU_NAVIGATION_FEATURE_FLAG]?.value === 'on';

  const { places, fetching, position, searchManager } = props;
  let { bounds, boundsFit } = searchV2;

  const search = { q: searchManager.q, location: searchManager.location, position: props.position, ...searchV2 };
  const userMapInteractionActiveRef = useRef(false);

  // Keep latest values in refs to avoid stale closures in map SDK event handlers
  const onBoundsChangeRef = useRef(props.onMapBoundsChange);
  useEffect(() => {
    onBoundsChangeRef.current = props.onMapBoundsChange;
  }, [props.onMapBoundsChange]);

  const boundsRef = useRef(bounds);
  useEffect(() => {
    boundsRef.current = bounds;
  }, [bounds]);

  const fetchingRef = useRef(fetching);
  useEffect(() => {
    fetchingRef.current = fetching;
  }, [fetching]);

  const styles = {
    width: '100%',
    height: `calc(100vh - ${props.mapTopPos ?? getMapPosition(isMobileView, useMegaMenu)})`,
    minHeight: '300px',
    zIndex: Z_INDEX.MAP,
    top: props.mapTopPos ?? getMapPosition(isMobileView, useMegaMenu),
  };

  const [carouselIndex, setCarouselIndex] = useState(0);

  const debouncedOnMapBoundsChange = useRef(
    debounce((boundsString) => {
      if (onBoundsChangeRef.current) {
        userMapInteractionActiveRef.current = true;
        onBoundsChangeRef.current(boundsString);
      }
    }, 300),
  ).current;

  interface MapEvent {
    originalEvent?: any;
    target: {
      getBounds: () => {
        _sw: { lat: number; lng: number };
        _ne: { lat: number; lng: number };
      };
    };
  }

  const handleMoveEnd = (e: MapEvent): void => {
    // does not come from user interaction
    if (!e.originalEvent) {
      userMapInteractionActiveRef.current = false;
      return; // skip programmatic move (e.g., from easeTo)
    }

    const { _sw, _ne } = e.target.getBounds();

    const targetBoundsObj: BoundsObject = {
      _southWest: { lat: _sw.lat, lng: _sw.lng },
      _northEast: { lat: _ne.lat, lng: _ne.lng },
    };

    const targetBounds: string = JSON.stringify(targetBoundsObj);
    const roundedTargetBounds: string | null = roundBounds(targetBounds, 7);

    const roundedSearchBounds: string | null = roundBounds(boundsRef.current, 7);

    if (!fetchingRef.current && roundedTargetBounds !== roundedSearchBounds) {
      debouncedOnMapBoundsChange(targetBounds);
    } else {
      userMapInteractionActiveRef.current = false;
    }
  };

  const handleZoomEnd = (e: MapEvent): void => {
    if (config.env === 'production' || !(search?.debugLocation?.length > 0)) {
      setCarouselIndex(0);
      return handleMoveEnd(e);
    }
  };

  const handleDragEnd = (e: MapEvent): void => {
    if (config.env === 'production' || !(search?.debugLocation?.length > 0)) {
      return handleMoveEnd(e);
    }
  };

  const columnClass = props.columnClass || '!w-5/12';

  const setting = {
    dragSpeed: 1.25,
    itemWidth: isServer ? (isMobile() ? breakpoints.sm : breakpoints.md) : window.innerWidth,
    itemSideOffsets: -15,
  };

  const itemStyle = {
    width: `${setting.itemWidth - 30}px`,
  };

  const handleTrackPlace = (place: SearchPlace) => {
    const screenName = search.isSistaminutenSearch ? 'deals_map' : 'search_results_map';
    trackPlaceClick(searchManager, place, carouselIndex, screenName);
    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.SERP_TO_PLACE_CLICK);
  };

  const getPlaceDetails = (place: SearchPlace) => {
    let image = getPlaceImage(place);
    image = getImagePreview(image, '60x60', place.about.slug);

    const { rating, contact, about } = place;
    const { subscriptionType } = about?.settings;
    const address = contact && contact.address ? contact.address : null;
    const showAddress = address && (address.city || address.street);
    const showRatings = shouldDisplayReviews(place) && rating && rating.count > 4;
    const availableTime = subscriptionType !== 'essential' && getFirstAvailableTime(place);
    const isExternalPlace = isProfilePageSubscription(subscriptionType);

    let href = `#details-${place.about.slug}-${place.id}`;

    if (searchV2.isSistaminutenSearch) {
      href += '?sistaminuten=1';
    }

    return (
      <a
        href={href}
        className="block min-h-[121px] w-full"
        onClick={() => {
          handleTrackPlace(place);
        }}>
        <div className="font-brown flex w-full flex-col gap-4">
          <div className="flex w-full flex-row">
            <span className="bg-black-100 mr-4 h-[60px] w-[60px] flex-none items-center overflow-hidden rounded-lg">
              {image && <img className="h-full object-contain" src={image} alt={place.about.name} />}
              {!image && (
                <span className="text-black-900 flex h-[60px] w-[60px] items-center justify-center text-3xl">
                  {place.about.name && place.about.name.charAt(0)}
                </span>
              )}
            </span>
            <div className="my-auto" style={{ minWidth: 0 }}>
              <h3 className="text-black-900 min-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold">
                {place.about.name}
              </h3>

              <div className="overflow-hidden text-ellipsis">
                {showRatings ? (
                  <StarRating count={rating.count} rating={rating.score} justCount={true} />
                ) : (
                  <span>&nbsp;</span>
                )}
              </div>

              {showAddress && (
                <div className="text-black-600 overflow-hidden text-ellipsis whitespace-nowrap text-sm">
                  <span>{address && address.street ? address.street.trim() : ''}</span>
                  {address && address.street && address.city ? ', ' : ''}
                  <span>{address && address.city ? address.city.trim() : ''}</span>
                </div>
              )}
              {availableTime ? availableTime : <span className="text-brand-700 block text-sm">&nbsp;</span>}
            </div>
          </div>
          {!isExternalPlace && <CardTags place={place} />}
          {isExternalPlace && <Label variant="external" size="lg" className="flex w-auto flex-none self-start" />}
        </div>
      </a>
    );
  };

  const getFirstAvailableTime = (place: SearchPlace) => {
    const matchedService = place.matchedServices && place.matchedServices[0] ? place.matchedServices[0] : null;
    const availability =
      matchedService && matchedService.availability && matchedService.availability[0]
        ? matchedService.availability[0]
        : null;

    if (!availability) {
      return null;
    }

    return (
      <span className="text-brand-500 block text-sm">
        {getDateFormattedForFirstAvailableTime(availability, place.timezone)}
      </span>
    );
  };

  const { mapBounds, center, boundsPadding } = useMemo(() => {
    return calculateMapViewParameters({
      bounds, // from searchV2
      boundsFit,
      places,
      position,
    });
  }, [bounds, places, position, boundsFit]); // Dependencies for memoization

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const debouncedMapMove = useRef(
    debounce((mapInstance, currentMapBounds, currentCenter, currentBoundsPadding) => {
      // This entire block runs AFTER the debounce delay (e.g., 800ms)
      if (userMapInteractionActiveRef.current) {
        userMapInteractionActiveRef.current = false; // Consume the flag for this data cycle
        return; // Do nothing, let user's map view persist.
      }

      const desiredPitch = mapInstance.getPitch(); // Use current pitch or set to 0/fixed value

      if (currentMapBounds && currentMapBounds.length > 0) {
        const cameraOptions = mapInstance.cameraForBounds(currentMapBounds, {
          padding: currentBoundsPadding,
          pitch: desiredPitch,
        });

        if (cameraOptions) {
          mapInstance.flyTo({
            ...cameraOptions,
            duration: 1000,
            speed: 0.8, // Optional: adjust flying speed
            essential: true,
          });
        } else {
          mapInstance.fitBounds(currentMapBounds, {
            padding: currentBoundsPadding,
            duration: 800,
          });
          if (desiredPitch > 0) {
            mapInstance.setPitch(desiredPitch);
          }
        }
      } else {
        mapInstance.setCenter(currentCenter);
        mapInstance.setZoom(10);
        mapInstance.setPitch(0); // Ensure flat for default view
      }
    }, 800), // Debounce delay of 800ms for map updates
  ).current;

  // --- Map Initialization Effect (runs once) ---
  useEffect(() => {
    if (!scriptLoaded) return;
    const { mapId = 'streets-v2', apiKey } = config.mapTiler;

    const maptilersdk = window.maptilersdk;

    if (mapContainerRef.current && maptilersdk && !mapInstanceRef.current) {
      maptilersdk.config.apiKey = apiKey;

      const nav = new maptilersdk.MaptilerNavigationControl({
        showCompass: false, // disable tilt view display 3D objects
        showZoom: true,
        visualizePitch: true,
        visualizeRotation: false,
      });

      const map = new maptilersdk.Map({
        container: mapContainerRef.current,
        apiKey: apiKey,
        style: mapId,
        maxZoom: 17,
        center: [18.07144, 59.32421], // Initialize to Stockholm center
        minZoom: 4, // Allow zoom out for initial view
        maptilerLogo: false, // show MapTiler logo
        attributionControl: false,
        navigationControl: false,
        geolocateControl: false,
      }).addControl(nav, 'top-left');

      // Store the map instance in the ref
      mapInstanceRef.current = map;

      // Add a load event listener
      map.on('ready', () => {
        userMapInteractionActiveRef.current = false;
        setMapLoaded(true);
      });

      map.on('dragend', handleDragEnd);
      map.on('zoomend', handleZoomEnd);

      // Cleanup function to remove the map when the component unmounts
      return () => {
        if (mapInstanceRef.current) {
          map.off('dragend', handleDragEnd);
          map.off('zoomend', handleZoomEnd);

          mapInstanceRef.current = null;
          setMapLoaded(false);
        }
      };
    }
  }, [scriptLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Handle view updates when bounds or places change ---
  useEffect(() => {
    if (mapLoaded && mapInstanceRef.current && !fetching) {
      debouncedMapMove(mapInstanceRef.current, mapBounds, center, boundsPadding);

      return () => {
        debouncedMapMove.cancel();
      };
    }
  }, [mapLoaded, fetching, mapBounds, center, boundsPadding, places, debouncedMapMove]);

  return (
    <div className={`sticky bg-cover ${props.mapWidth ?? columnClass}`} style={styles}>
      <div ref={mapContainerRef} style={{ height: styles.height }}></div>
      {mapLoaded && mapInstanceRef.current && (
        <>
          {/* ... (MapTiler and other UI markers) ... */}
          <MapTilerMarkers
            map={mapInstanceRef.current}
            places={places}
            setCarouselIndex={setCarouselIndex}
            carouselIndex={carouselIndex}
            getPlaceDetails={getPlaceDetails}
          />

          {/* ... (Carousel and other UI elements) ... */}
          {props.isMobileView && places && places.length > 0 && (
            <div
              id="place-details"
              className={classnames(placeStyles.placeDetails, props.carouselClassname ?? '')}
              style={{ opacity: 0.9 }}>
              <Carousel _data={places} {...setting} carouselIndex={carouselIndex} setCarouselIndex={setCarouselIndex}>
                {places.map((place: SearchPlace, _i: number) => (
                  <div className={placeStyles.carouselItem} key={place.id || _i} style={{ ...itemStyle }}>
                    <Card>
                      <div className={placeStyles.carouselItemDetails}>{getPlaceDetails(place)}</div>
                    </Card>
                  </div>
                ))}
              </Carousel>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const MapTiler = withMobileView(DynamicMap);

export default MapTiler;
